import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Video, Users, Paperclip, Send, PenLine, CircleDot, Lock, ChevronLeft, MessageSquare, FileText, Clock, ExternalLink, ChevronDown, ChevronRight, Wrench, GitBranch, ClipboardList, CalendarPlus, ImageIcon, HandHelping, Sparkles, Pencil, X, Check, FileDown, ShieldCheck, CalendarClock } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/** FormalMeetingRoom — iter26 redesign.
 *  Route: /app/meetings/:id
 *  - Collapsible action rows with per-action attachments, activity, chat, sub-tasks + buttons
 *  - Action ID / open links use target="_blank" (never leaves the meeting page)
 *  - Rename "General notes" → "Additional Topics" (with subject + notes)
 *  - Bottom scheduler for follow-up meetings with full traceability
 *  - Visible link chain to parent/child meetings for auditability
 */
export default function FormalMeetingRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [poll, setPoll] = useState(0);

  const load = async () => {
    try {
      const r = await api.get(`/formal-meetings/${id}`);
      setM(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Meeting not found");
      nav("/app/meetings");
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!m || m.status !== "in_progress") return;
    const t = setInterval(() => setPoll((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [m]);
  useEffect(() => { if (poll > 0) load(); }, [poll]);

  const iAttended = useMemo(() =>
    (m?.notes || []).some((n) => n.kind === "attendance" && n.user_id === user?.id),
  [m, user]);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const attend = async () => {
    setBusy(true);
    try {
      await api.post(`/formal-meetings/${id}/attend`);
      toast.success("Attendance recorded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const close = async () => {
    if (!window.confirm("Close this meeting and generate the report?\n\nThe minutes will be locked, and a signed report will be emailed to every attendee plus any addresses set for this meeting type in Destinations. This cannot be undone.")) return;
    try {
      await api.post(`/formal-meetings/${id}/close`);
      toast.success("Meeting closed · report emailed to attendees + destinations");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (!m) return <div className="p-8 text-center font-mono text-[11px] uppercase tracking-wider text-[#1a1a1a]/55">Loading…</div>;

  const styleLabel = ({
    management_review: "Monthly Management Review",
    problem_solving: "Problem Solving",
    effectiveness_review: "Effectiveness Review",
    safety: "Safety Stand-Down",
    custom: "Custom",
  })[m.style] || m.style;

  const isOwner = m.created_by === user?.id;
  const isClosed = m.status === "closed";
  const isScheduled = m.status === "scheduled";
  const canClose = isOwner && !isClosed && !isScheduled;
  const canStart = isOwner && isScheduled;   // iter58 — start button for scheduled meetings

  const startMeeting = async () => {
    if (!window.confirm("Start this meeting now? Attendees will receive email invites.")) return;
    try {
      await api.post(`/formal-meetings/${m.id}/start`);
      toast.success("Meeting started — invites sent.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    }
  };
  const notesByAction = m.notes.reduce((acc, n) => {
    if (n.kind !== "note" && n.kind !== "file_attached") return acc;
    const key = n.action_id || "__general__";
    acc[key] = acc[key] || [];
    acc[key].push(n);
    return acc;
  }, {});
  const attendanceLog = m.notes.filter((n) => n.kind === "attendance");

  const activeActions = m.actions.filter((a) => (m.action_ids || []).includes(a.id));
  const archivedActions = m.actions.filter((a) => (m.archived_action_ids || []).includes(a.id));

  return (
    <div className="p-4 lg:p-8 space-y-5 max-w-6xl mx-auto">
      <div>
        <Link to="/app/meetings" className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a]/55 hover:text-[#ff4500] inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Back to meetings
        </Link>
      </div>

      <section className={`rounded-2xl p-6 text-white overflow-hidden relative ${isClosed ? "bg-[#1a1a1a]" : "hero-navy"}`}>
        <div className="absolute inset-0 dotted opacity-15" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-3">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white ${isClosed ? "bg-white/10" : "bg-[#ff4500]"}`}>
            <Video className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5f15]">// {m.display_id} · {styleLabel}</span>
              {!isClosed && !isScheduled && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#ff4500] px-2 py-0.5 rounded-full animate-pulse"><CircleDot className="w-2 h-2" /> Live</span>}
              {isScheduled && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#d6a900] px-2 py-0.5 rounded-full"><CalendarClock className="w-2 h-2" /> Scheduled</span>}
              {isClosed && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-white/15 px-2 py-0.5 rounded-full"><Lock className="w-2 h-2" /> Closed</span>}
              {m.parent_meeting && (
                <a href={`/app/meetings/${m.parent_meeting.id}`} target="_blank" rel="noreferrer"
                   data-testid="parent-meeting-link"
                   className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#ff5f15]/20 border border-[#ff5f15]/40 px-2 py-0.5 rounded-full hover:bg-[#ff5f15]/30">
                  <GitBranch className="w-2.5 h-2.5" /> Follow-up of {m.parent_meeting.display_id}
                </a>
              )}
            </div>
            <h1 className="font-display font-black text-2xl md:text-3xl mt-1">{m.title}</h1>
            <div className="text-[11px] font-mono text-white/60 mt-1 flex items-center gap-3 flex-wrap">
              <span><Clock className="w-3 h-3 inline mr-1" /> Started {formatDate(m.started_at)}</span>
              {m.closed_at && <span>Closed {formatDate(m.closed_at)}</span>}
              <span>Owner: {m.creator?.name || m.created_by_name}</span>
            </div>
            {(m.child_meetings || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="child-meetings">
                <span className="text-[10px] uppercase tracking-wider text-white/55 font-mono">Follow-ups:</span>
                {m.child_meetings.map((c) => (
                  <a key={c.id} href={`/app/meetings/${c.id}`} target="_blank" rel="noreferrer"
                     data-testid={`child-meeting-${c.id}`}
                     className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-white/10 border border-white/25 px-2 py-0.5 rounded-full hover:bg-white/20">
                    {c.display_id} · {c.scheduled_for?.slice(0,10)} <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-stretch md:items-end">
            {canStart && (
              <button data-testid="start-meeting-btn" onClick={startMeeting}
                className="btn-pill btn-pill-primary text-[13px]">
                <CircleDot className="w-3 h-3" /> Start meeting now
              </button>
            )}
            {isScheduled && isOwner && (
              <button data-testid="reschedule-meeting-btn" onClick={() => setRescheduleOpen(true)}
                className="btn-pill btn-pill-ghost text-[11px] !text-white !border-white/30 hover:!bg-white/10">
                <Pencil className="w-3 h-3" /> Edit / reschedule
              </button>
            )}
            {!isScheduled && !isClosed && isOwner && (
              <button data-testid="edit-live-meeting-btn" onClick={() => setRescheduleOpen(true)}
                className="btn-pill btn-pill-ghost text-[11px] !text-white !border-white/30 hover:!bg-white/10"
                title="Edit meeting details (title, agenda) — allowed until closed">
                <Pencil className="w-3 h-3" /> Edit details
              </button>
            )}
            {!iAttended && !isClosed && !isScheduled && (
              <button data-testid="attend-btn" onClick={attend} disabled={busy}
                className="btn-pill btn-pill-primary"><PenLine className="w-3 h-3" /> Attend meeting</button>
            )}
            {iAttended && !isClosed && !isScheduled && <span data-testid="attended-badge" className="text-[10px] uppercase tracking-wider font-bold text-[#1a8754] bg-[#1a8754]/20 border border-[#1a8754]/40 px-3 py-1.5 rounded-full inline-flex items-center gap-1"><PenLine className="w-3 h-3" /> Attending</span>}
            {canClose && (
              <button data-testid="close-meeting-btn" onClick={close}
                className="btn-pill btn-pill-ghost text-[11px] !text-white !border-white/30 hover:!bg-white/10">
                <FileDown className="w-3 h-3" /> Close meeting &amp; generate report
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-5">
        <div className="space-y-4">
          {activeActions.length === 0 && archivedActions.length === 0 && (
            <div className="surface-card rounded-2xl p-6 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
              No actions were linked to this meeting.
            </div>
          )}
          {activeActions.map((a) => (
            <ActionAccordion key={a.id} action={a} meetingId={id} notes={notesByAction[a.id] || []}
              locked={isClosed} onNoteAdded={load} currentUser={user} />
          ))}
          {archivedActions.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a1a1a]/55 mt-6 mb-2">// Archived context</div>
              {archivedActions.map((a) => (
                <ActionAccordion key={a.id} action={a} meetingId={id} notes={notesByAction[a.id] || []}
                  locked={isClosed} onNoteAdded={load} currentUser={user} archived />
              ))}
            </div>
          )}

          <AdditionalTopicsBox meetingId={id} notes={notesByAction["__general__"] || []}
            locked={isClosed} onNoteAdded={load} currentUser={user} />

          {/* iter28 — Servant Leadership + Continuous Improvement */}
          <SectionNotesBox
            meetingId={id}
            locked={isClosed}
            notes={(m.notes || []).filter((n) => n.section === "servant_leadership")}
            section="servant_leadership"
            title="Servant Leadership"
            subtitle="Share a few ways attendees witnessed or demonstrated servant leadership. Give credit to a teammate, or float an idea for a future demonstration."
            tint="#1a8754"
            icon={HandHelping}
            onSaved={load}
            currentUser={user}
          />
          <SectionNotesBox
            meetingId={id}
            locked={isClosed}
            notes={(m.notes || []).filter((n) => n.section === "continuous_improvement")}
            section="continuous_improvement"
            title="Continuous Improvement"
            subtitle="Capture CI ideas that were witnessed, shown or shared — especially from the folks on the floor."
            tint="#d6a900"
            icon={Sparkles}
            onSaved={load}
            currentUser={user}
          />

          {!isClosed && isOwner && (
            <ScheduleFollowupPanel meetingId={id} actions={activeActions.concat(archivedActions)}
              attendees={m.attendees} onScheduled={load} />
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 self-start">
          <div className="surface-card rounded-2xl overflow-hidden">
            <div className="bg-[#1a1a1a] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-1">
              <Users className="w-3 h-3" /> Attendees ({m.attendees.length})
            </div>
            <ul className="divide-y divide-[#1a1a1a]/06 max-h-64 overflow-auto scrollbar-thin">
              {m.attendees.map((u) => {
                const att = attendanceLog.find((n) => n.user_id === u.id);
                return (
                  <li key={u.id} className="px-4 py-2 flex items-center gap-2" data-testid={`attendee-${u.id}`}>
                    <div className="w-7 h-7 rounded-lg bg-[#f0ede6] text-[#1a1a1a] font-display font-black text-[11px] flex items-center justify-center">
                      {(u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium text-[#1a1a1a] truncate">{u.name}</div>
                      {att ? (
                        <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a8754]">Signed {new Date(att.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      ) : (
                        <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/40">Not yet attended</div>
                      )}
                    </div>
                  </li>
                );
              })}
              {m.attendees.length === 0 && <li className="px-4 py-4 text-[11px] text-[#1a1a1a]/55">No attendees invited.</li>}
            </ul>
          </div>

          {m.attachments.length > 0 && (
            <div className="surface-card rounded-2xl overflow-hidden">
              <div className="bg-[#1a1a1a] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachments ({m.attachments.length})
              </div>
              <ul className="divide-y divide-[#1a1a1a]/06">
                {m.attachments.map((f) => <FileRow key={f.id} f={f} />)}
              </ul>
            </div>
          )}

          <div className="surface-card rounded-2xl overflow-hidden">
            <div className="bg-[#1a1a1a] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> Highlight reel
            </div>
            <HighlightReel notes={m.notes || []} actions={m.actions || []} />
          </div>

          {m.style === "management_review" && <UpcomingVVPanel />}
          {(m.agenda_items && m.agenda_items.length > 0) && <AgendaItemsPanel items={m.agenda_items} meetingId={m.id} isLive={!isScheduled && !isClosed} />}
          <AttachedVVPanel meetingId={m.id} orgOnlyOwner={m.owner_id} />
        </aside>
      </div>
      {rescheduleOpen && (
        <RescheduleMeetingDialog meeting={m} onClose={() => setRescheduleOpen(false)} onSaved={load} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-action collapsible accordion (iter26)
// ---------------------------------------------------------------------------
function ActionAccordion({ action, meetingId, notes, locked, onNoteAdded, archived, currentUser }) {
  const [open, setOpen] = useState(false);
  const testId = `action-box-${action.id}`;
  const actionUrl = `/app/actions/${action.id}`;
  return (
    <div className={`surface-card rounded-2xl overflow-hidden ${archived ? "opacity-70" : ""}`} data-testid={testId}>
      {/* Header — always visible, click to expand */}
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        className="w-full px-4 py-3 flex items-start gap-2 text-left hover:bg-[#faf8f4] transition-colors">
        <div className="w-8 h-8 rounded-lg bg-[#ff4500] text-white font-display font-black text-[10px] flex items-center justify-center flex-shrink-0">
          {action.display_id?.split("-")[1] || "AX"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <a href={actionUrl} target="_blank" rel="noreferrer"
               data-testid={`${testId}-id-link`}
               onClick={(e) => e.stopPropagation()}
               className="font-mono text-[10px] font-bold text-[#ff4500] hover:underline inline-flex items-center gap-0.5">
              {action.display_id} <ExternalLink className="w-2.5 h-2.5" />
            </a>
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/60">{action.status}</span>
            {action.priority && action.priority !== "normal" && <span className="text-[9px] uppercase text-white bg-[#ff4500] px-1.5 rounded-full">{action.priority}</span>}
            {action.risk_score && <span className="text-[9px] uppercase text-white bg-[#1a1a1a] px-1.5 rounded-full">R {action.risk_score}</span>}
            {notes.length > 0 && <span className="text-[9px] uppercase font-bold text-[#1a1a1a]/55">{notes.length} meeting note{notes.length === 1 ? "" : "s"}</span>}
          </div>
          <div className="font-display font-bold text-sm text-[#1a1a1a] truncate">{action.title}</div>
          {action.description && !open && <div className="text-[11px] text-[#1a1a1a]/65 line-clamp-1">{action.description}</div>}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#1a1a1a]/40 flex-shrink-0 mt-1" /> : <ChevronRight className="w-4 h-4 text-[#1a1a1a]/40 flex-shrink-0 mt-1" />}
      </button>

      {open && (
        <div className="border-t border-[#1a1a1a]/06">
          {action.description && (
            <div className="px-4 pt-3 text-[12px] text-[#1a1a1a]/75 whitespace-pre-wrap">{action.description}</div>
          )}

          {/* Action buttons — Trigger Action / 5-Why / Work Request */}
          <ActionButtonRow action={action} testId={testId} />

          {/* Photos + Files attached to the action */}
          {((action._photos || []).length > 0 || (action._files || []).length > 0) && (
            <div className="px-4 pt-2 pb-3" data-testid={`${testId}-attachments`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#1a1a1a]/55 mb-1.5">// Attached files &amp; photos</div>
              <div className="flex flex-wrap gap-1.5">
                {(action._photos || []).map((p) => <PhotoThumb key={p.id} f={p} />)}
                {(action._files || []).map((f) => <FileChip2 key={f.id} f={f} />)}
              </div>
            </div>
          )}

          {/* Sub-tasks */}
          {(action.sub_tasks || []).length > 0 && (
            <div className="px-4 pb-3" data-testid={`${testId}-subtasks`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#1a1a1a]/55 mb-1.5">// Sub-tasks ({action.sub_tasks.length})</div>
              <ul className="space-y-1">
                {action.sub_tasks.map((st) => (
                  <li key={st.id} className="flex items-center gap-2 text-[12px] bg-[#faf8f4] rounded-md px-2 py-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${["closed","done","completed","verified"].includes((st.status||"").toLowerCase()) ? "bg-[#1a8754]" : "bg-[#d6a900]"}`} />
                    <span className="flex-1 text-[#1a1a1a] truncate">{st.title}</span>
                    {st.due_date && <span className="text-[10px] font-mono text-[#1a1a1a]/55">due {st.due_date}</span>}
                    <span className="text-[10px] uppercase font-bold text-[#1a1a1a]/60">{st.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Historical activity from the action itself (comments, status changes, meeting_comments) */}
          {(action._activity || []).length > 0 && (
            <div className="px-4 pb-3" data-testid={`${testId}-activity`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#1a1a1a]/55 mb-1.5">// Previous activity ({(action._activity || []).length})</div>
              <ul className="space-y-1.5 max-h-48 overflow-auto scrollbar-thin">
                {action._activity.slice(-15).map((row) => (
                  <li key={row.id} className="bg-white border border-[#1a1a1a]/06 rounded-md px-2 py-1.5">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[9px] font-mono uppercase tracking-wider font-bold text-[#1a1a1a]/55">{row.kind}</span>
                      <span className="text-[9px] font-mono text-[#1a1a1a]/40">{new Date(row.created_at).toLocaleString()}</span>
                      <span className="text-[9px] font-mono text-[#1a1a1a]/55 ml-auto">— {row.user_name}</span>
                    </div>
                    {row.text && <div className="text-[12px] text-[#1a1a1a] whitespace-pre-wrap">{row.text}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Chat messages linked to this action */}
          {(action._chat || []).length > 0 && (
            <div className="px-4 pb-3" data-testid={`${testId}-chat`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#1a1a1a]/55 mb-1.5">// Linked hub chat ({(action._chat || []).length})</div>
              <ul className="space-y-1.5 max-h-40 overflow-auto scrollbar-thin">
                {(action._chat || []).slice(-10).map((c) => (
                  <li key={c.id} className="bg-[#fff1ea] border border-[#ff4500]/15 rounded-md px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <MessageSquare className="w-2.5 h-2.5 text-[#ff4500]" />
                      <span className="font-display font-bold text-[11px] text-[#1a1a1a]">{c.user_name}</span>
                      <span className="text-[9px] font-mono text-[#1a1a1a]/45 ml-auto">{new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="text-[12px] text-[#1a1a1a] whitespace-pre-wrap">{c.message}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Meeting-specific notes on this action */}
          {notes.length > 0 && (
            <div className="px-4 pb-3" data-testid={`${testId}-meeting-notes`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] mb-1.5">// This meeting&apos;s discussion ({notes.length})</div>
              <ul className="space-y-1.5">
                {notes.map((n) => <MeetingNoteRow key={n.id} n={n} meetingId={meetingId} currentUser={currentUser} locked={locked} onSaved={onNoteAdded} />)}
              </ul>
            </div>
          )}

          {/* New comment composer */}
          {!locked && (
            <MeetingNoteComposer meetingId={meetingId} actionId={action.id} testId={testId} onSaved={onNoteAdded} />
          )}
          {locked && <LockedFooter />}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trigger Action / 5-Why / Work Request buttons — deep-link into the action's
// detail page in a NEW TAB so the meeting stays open.
// ---------------------------------------------------------------------------
function ActionButtonRow({ action, testId }) {
  const openTab = (hash) => window.open(`/app/actions/${action.id}${hash}`, "_blank", "noopener,noreferrer");
  return (
    <div className="px-4 pt-3 pb-2 flex flex-wrap gap-2 border-b border-[#1a1a1a]/06" data-testid={`${testId}-buttons`}>
      <button type="button" onClick={() => openTab("#trigger-action")}
        data-testid={`${testId}-btn-trigger-action`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#ff4500] text-white px-3 py-1.5 rounded-full hover:bg-[#cc3500]">
        <ClipboardList className="w-3 h-3" /> Trigger action
      </button>
      <button type="button" onClick={() => openTab("#five-whys")}
        data-testid={`${testId}-btn-five-whys`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#1a1a1a] text-white px-3 py-1.5 rounded-full hover:bg-[#2a2a2a]">
        <GitBranch className="w-3 h-3" /> 5-Whys
      </button>
      <button type="button" onClick={() => openTab("#work-request")}
        data-testid={`${testId}-btn-work-request`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-white text-[#1a1a1a] border border-[#1a1a1a]/25 px-3 py-1.5 rounded-full hover:bg-[#1a1a1a] hover:text-white">
        <Wrench className="w-3 h-3" /> Work request
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Additional Topics — replaces "Anything not tied to a specific action"
// Adds subject/title field + notes + Trigger/5-Why/Work-request buttons
// ---------------------------------------------------------------------------
function AdditionalTopicsBox({ meetingId, notes, locked, onNoteAdded, currentUser }) {
  const testId = "additional-topics";
  // Group notes by topic_subject (fallback "Untitled")
  const grouped = useMemo(() => {
    const g = {};
    notes.forEach((n) => {
      const k = (n.topic_subject || "Untitled").trim() || "Untitled";
      (g[k] = g[k] || []).push(n);
    });
    return Object.entries(g);
  }, [notes]);
  return (
    <div className="surface-card rounded-2xl overflow-hidden border-[#ff4500]/20" data-testid={testId}>
      <div className="px-4 py-3 border-b border-[#1a1a1a]/06 bg-[#fff1ea] flex items-start gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#ff4500] text-white flex items-center justify-center"><MessageSquare className="w-4 h-4" /></div>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Meeting-wide discussion</div>
          <div className="font-display font-black text-lg text-[#1a1a1a]">Additional Topics</div>
          <div className="text-[11px] text-[#1a1a1a]/65">Anything worth capturing that isn&apos;t tied to a specific action. Add a subject so it stays organized.</div>
        </div>
      </div>

      {grouped.length > 0 && (
        <div className="divide-y divide-[#1a1a1a]/06">
          {grouped.map(([subject, list]) => (
            <div key={subject} className="px-4 py-3" data-testid={`topic-thread-${subject.slice(0,30)}`}>
              <div className="font-display font-bold text-sm text-[#1a1a1a] mb-2">{subject}</div>
              <ul className="space-y-1.5">
                {list.map((n) => <MeetingNoteRow key={n.id} n={n} meetingId={meetingId} currentUser={currentUser} locked={locked} onSaved={onNoteAdded} />)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {!locked && (
        <MeetingNoteComposer meetingId={meetingId} actionId={null} testId={testId} onSaved={onNoteAdded} allowSubject
          hasButtonRow />
      )}
      {locked && <LockedFooter />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Note composer — text + optional subject + optional trigger buttons
// ---------------------------------------------------------------------------
function MeetingNoteComposer({ meetingId, actionId, testId, onSaved, allowSubject, hasButtonRow }) {
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [fileIds, setFileIds] = useState([]);
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim() && fileIds.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/formal-meetings/${meetingId}/notes`, {
        action_id: actionId,
        text,
        file_ids: fileIds,
        topic_subject: allowSubject ? (subject.trim() || null) : null,
      });
      setText("");
      setSubject("");
      setFileIds([]);
      setShowFileUploader(false);
      toast.success("Comment added — signed & timestamped");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const openTab = (path) => window.open(path, "_blank", "noopener,noreferrer");
  return (
    <div className="px-4 py-3 border-t border-[#1a1a1a]/06 bg-[#faf8f4] space-y-2">
      {allowSubject && (
        <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120}
          placeholder="Subject / topic title (optional — helps thread the discussion)"
          data-testid={`${testId}-subject`}
          className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm font-medium focus:outline-none focus:border-[#ff4500]" />
      )}
      <textarea data-testid={`${testId}-input`} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Add a note — will be signed with your name + timestamp…"
        className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-16 focus:outline-none focus:border-[#1a1a1a]" />
      {showFileUploader && (
        <PhotoUploader photoIds={fileIds} onChange={setFileIds} testIdPrefix={`${testId}-file`} />
      )}
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowFileUploader((v) => !v)} type="button"
            className="text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 hover:text-[#ff4500] inline-flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> {showFileUploader ? "hide" : "attach"} files {fileIds.length ? `(${fileIds.length})` : ""}
          </button>
          {hasButtonRow && (
            <>
              <button type="button" onClick={() => openTab("/app/actions?new=1")}
                data-testid={`${testId}-btn-trigger-action`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#ff4500] text-white px-3 py-1.5 rounded-full hover:bg-[#cc3500]">
                <ClipboardList className="w-3 h-3" /> Trigger action
              </button>
              <button type="button" onClick={() => openTab("/app/actions?five_why=1")}
                data-testid={`${testId}-btn-five-whys`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[#1a1a1a] text-white px-3 py-1.5 rounded-full hover:bg-[#2a2a2a]">
                <GitBranch className="w-3 h-3" /> 5-Whys
              </button>
              <button type="button" onClick={() => openTab("/app/actions?work_request=1")}
                data-testid={`${testId}-btn-work-request`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-white text-[#1a1a1a] border border-[#1a1a1a]/25 px-3 py-1.5 rounded-full hover:bg-[#1a1a1a] hover:text-white">
                <Wrench className="w-3 h-3" /> Work request
              </button>
            </>
          )}
        </div>
        <button data-testid={`${testId}-send`} onClick={send} disabled={busy}
          className="btn-pill btn-pill-primary text-[11px]">
          <Send className="w-3 h-3" /> {busy ? "Sending…" : "Add note"}
        </button>
      </div>
    </div>
  );
}

function LockedFooter() {
  return (
    <div className="px-4 py-2 border-t border-[#1a1a1a]/06 bg-[#f7f5f2] text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 inline-flex items-center gap-1">
      <Lock className="w-3 h-3" /> Meeting closed — notes locked for compliance.
    </div>
  );
}

function MeetingNoteRow({ n, meetingId, currentUser, locked, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(n.text || "");
  const [expanded, setExpanded] = useState(false); // iter28 — start collapsed, click to expand full text
  const [busy, setBusy] = useState(false);
  const canEdit = !locked && meetingId && currentUser && n.user_id === currentUser?.id
    && ["note", "file_attached", "section_note"].includes(n.kind);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/formal-meetings/${meetingId}/notes/${n.id}`, { text });
      toast.success("Note updated");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <li className="bg-white border border-[#1a1a1a]/06 rounded-md px-3 py-2" data-testid={`note-${n.id}`}>
      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
        <span className="font-display font-bold text-[12px] text-[#1a1a1a]">{n.user_name}</span>
        {n.employee_id && <span className="text-[9px] font-mono uppercase text-[#1a1a1a]/45">· {n.employee_id}</span>}
        {n.edited_at && <span className="text-[9px] font-mono uppercase text-[#d6a900]/80">· edited</span>}
        <span className="text-[9px] font-mono uppercase text-[#1a1a1a]/45 ml-auto">{new Date(n.created_at).toLocaleString()}</span>
        {canEdit && !editing && (
          <button type="button" onClick={() => setEditing(true)} data-testid={`note-${n.id}-edit`}
            className="text-[9px] uppercase tracking-wider font-bold text-[#ff4500] hover:text-[#cc3500] inline-flex items-center gap-0.5">
            <Pencil className="w-2.5 h-2.5" /> edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1 space-y-1">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
            data-testid={`note-${n.id}-edit-input`}
            className="w-full bg-white border border-[#ff4500]/40 rounded-md px-2 py-1 text-[13px] focus:outline-none focus:border-[#ff4500]" />
          <div className="flex gap-1.5 justify-end">
            <button type="button" onClick={() => { setEditing(false); setText(n.text || ""); }}
              className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/60 inline-flex items-center gap-0.5"><X className="w-3 h-3" /> cancel</button>
            <button type="button" onClick={save} disabled={busy}
              data-testid={`note-${n.id}-edit-save`}
              className="text-[10px] uppercase tracking-wider font-bold text-white bg-[#ff4500] px-2 py-0.5 rounded-full inline-flex items-center gap-0.5"><Check className="w-3 h-3" /> save</button>
          </div>
        </div>
      ) : (
        <>
          {/* iter28 — for Additional Topics, keep the "card" tight: click to expand full text.
              Short notes (<80 chars) always show inline. Section-notes always show inline. */}
          {n.text && (n.text.length <= 80 || n.kind === "section_note" || expanded ? (
            <div className="text-[13px] text-[#1a1a1a] whitespace-pre-wrap">{n.text}</div>
          ) : (
            <button type="button" onClick={() => setExpanded(true)} data-testid={`note-${n.id}-expand`}
              className="text-[13px] text-left text-[#1a1a1a] hover:text-[#ff4500] w-full truncate">
              {n.text.slice(0, 80)}… <span className="text-[10px] uppercase font-mono text-[#ff4500]">click to expand</span>
            </button>
          ))}
          {expanded && n.text && n.text.length > 80 && (
            <button type="button" onClick={() => setExpanded(false)}
              className="text-[9px] uppercase font-mono text-[#1a1a1a]/50 hover:text-[#ff4500]">collapse</button>
          )}
        </>
      )}
      {n.file_ids?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {n.file_ids.map((fid) => <FileChip fid={fid} key={fid} />)}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Iter28 — HighlightReel: bullet-point activity summary (not verbatim note dump)
// ---------------------------------------------------------------------------
function HighlightReel({ notes, actions }) {
  const items = useMemo(() => {
    // Build structured event bullets. We DO NOT repeat the full note text — we just
    // announce the who / what happened. Full text lives in the Additional-Topics /
    // action-accordion sections and is expandable there.
    const displayIdMap = Object.fromEntries((actions || []).map((a) => [a.id, a.display_id]));
    const out = notes.map((n) => {
      const when = new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      let text = n.text;
      let tint = "#1a1a1a/55";
      switch (n.kind) {
        case "created":
          text = `${n.user_name} started the meeting`; tint = "#1a1a1a"; break;
        case "attendance":
          text = `${n.user_name} joined the meeting`; tint = "#1a8754"; break;
        case "guest_attendance":
          text = `${n.user_name} attended as guest`; tint = "#1a8754"; break;
        case "note":
          text = `${n.user_name} added note${n.topic_subject ? ` "${n.topic_subject}"` : ""}`; tint = "#ff4500"; break;
        case "file_attached":
          text = `${n.user_name} attached ${n.file_ids?.length || 1} file${(n.file_ids?.length || 1) === 1 ? "" : "s"}`; tint = "#ff4500"; break;
        case "section_note":
          text = `${n.user_name} shared a ${n.section_label || "section"} note`; tint = "#d6a900"; break;
        case "action_created":
          text = `${n.user_name} triggered new action`; tint = "#ff4500"; break;
        case "action_updated":
          text = `${n.user_name} updated an action`; tint = "#1a1a1a"; break;
        case "meeting_comment":
          text = `${n.user_name} commented on ${displayIdMap[n.action_id] || "an action"}`; tint = "#ff4500"; break;
        case "followup_scheduled":
          text = `${n.user_name} scheduled a follow-up meeting`; tint = "#d6a900"; break;
        case "report_dispatched":
          text = `Report emailed to attendees + destinations`; tint = "#1a8754"; break;
        case "closed":
          text = `${n.user_name} closed the meeting`; tint = "#ff4500"; break;
        default: break;
      }
      return { id: n.id, text, when, tint };
    });
    return out.reverse().slice(0, 60);
  }, [notes, actions]);
  return (
    <ul className="divide-y divide-[#1a1a1a]/06 max-h-80 overflow-auto scrollbar-thin" data-testid="highlight-reel">
      {items.length === 0 && <li className="px-3 py-4 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/45">No activity yet.</li>}
      {items.map((it) => (
        <li key={it.id} className="px-3 py-1.5 flex items-start gap-2">
          <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: it.tint }} />
          <div className="flex-1 min-w-0 flex items-baseline gap-2">
            <div className="text-[12px] text-[#1a1a1a] truncate">{it.text}</div>
            <span className="text-[9px] font-mono text-[#1a1a1a]/45 flex-shrink-0 ml-auto">{it.when}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Iter28 — SectionNotesBox: Servant Leadership + Continuous Improvement
// 3 slots per section, batched Add Note button, optional but encouraged.
// ---------------------------------------------------------------------------
function SectionNotesBox({ meetingId, section, title, subtitle, tint, icon: Icon, notes, locked, onSaved, currentUser }) {
  const [slots, setSlots] = useState(["", "", ""]);
  const [busy, setBusy] = useState(false);
  const filled = slots.filter((s) => s.trim()).length;
  const addNotes = async () => {
    const payload = slots.map((s) => s.trim()).filter((s) => s);
    if (payload.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/formal-meetings/${meetingId}/section-notes`, {
        section,
        notes: payload,
      });
      toast.success(`${payload.length} ${title} note${payload.length === 1 ? "" : "s"} added`);
      setSlots(["", "", ""]);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="surface-card rounded-2xl overflow-hidden" data-testid={`section-box-${section}`}
      style={{ borderColor: `${tint}30` }}>
      <div className="px-4 py-3 border-b border-[#1a1a1a]/06 flex items-start gap-2" style={{ background: `${tint}12` }}>
        <div className="w-8 h-8 rounded-lg text-white flex items-center justify-center" style={{ background: tint }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: tint }}>// Reflection · optional but encouraged</div>
          <div className="font-display font-black text-lg text-[#1a1a1a]">{title}</div>
          <div className="text-[11px] text-[#1a1a1a]/65">{subtitle}</div>
        </div>
      </div>

      {notes.length > 0 && (
        <ul className="divide-y divide-[#1a1a1a]/06 px-4 py-2 space-y-1">
          {notes.map((n) => (
            <MeetingNoteRow key={n.id} n={n} meetingId={meetingId} currentUser={currentUser} locked={locked} onSaved={onSaved} />
          ))}
        </ul>
      )}

      {!locked && (
        <div className="px-4 py-3 space-y-2 bg-[#faf8f4] border-t border-[#1a1a1a]/06">
          {slots.map((val, i) => (
            <input key={i} value={val} onChange={(e) => setSlots(slots.map((s, j) => j === i ? e.target.value : s))}
              data-testid={`section-${section}-slot-${i}`}
              placeholder={`Note ${i + 1}${i === 0 ? " (share one observation…)" : " (optional)"}`}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-[#1a1a1a]" />
          ))}
          <div className="flex justify-between items-center">
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
              {filled === 0 ? "Fill any 1, 2, or all 3" : `${filled} of 3 filled`}
            </span>
            <button type="button" onClick={addNotes} disabled={busy || filled === 0}
              data-testid={`section-${section}-submit`}
              className="btn-pill btn-pill-primary text-[11px] disabled:opacity-40">
              <Send className="w-3 h-3" /> {busy ? "Saving…" : `Add ${filled || ""} note${filled === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}
      {locked && <LockedFooter />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule Follow-up panel — bottom of meeting page (owner only, meeting live)
// ---------------------------------------------------------------------------
function ScheduleFollowupPanel({ meetingId, actions, attendees, onScheduled }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [selectedAttendees, setSelectedAttendees] = useState(new Set());
  const [carryOverIds, setCarryOverIds] = useState(new Set());
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [addCalendars, setAddCalendars] = useState(true);
  const [style, setStyle] = useState("problem_solving");
  const [busy, setBusy] = useState(false);

  const toggleSet = (setter, val) => (id) => {
    const next = new Set(val);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const schedule = async () => {
    if (!title.trim()) { toast.error("Please give the follow-up meeting a title"); return; }
    if (!scheduledFor) { toast.error("Please choose when this meeting should happen"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/formal-meetings/${meetingId}/schedule-followup`, {
        title, style, subject,
        scheduled_for: scheduledFor,
        attendee_user_ids: [...selectedAttendees],
        carry_over_action_ids: [...carryOverIds],
        notify_email: notifyEmail,
        add_to_calendars: addCalendars,
      });
      toast.success(`Follow-up ${r.data.display_id} scheduled${notifyEmail ? " · email dispatched" : ""}`);
      setTitle(""); setSubject(""); setScheduledFor("");
      setSelectedAttendees(new Set()); setCarryOverIds(new Set());
      setOpen(false);
      onScheduled?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to schedule");
    } finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-2xl overflow-hidden border-[#1a1a1a]/15" data-testid="schedule-followup-panel">
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid="schedule-followup-toggle"
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-[#faf8f4] transition-colors">
        <div className="w-8 h-8 rounded-lg bg-[#d6a900] text-white flex items-center justify-center flex-shrink-0"><CalendarPlus className="w-4 h-4" /></div>
        <div className="flex-1 text-left">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a37e00]">// Reconvene when needed</div>
          <div className="font-display font-black text-base text-[#1a1a1a]">Schedule a follow-up meeting</div>
          <div className="text-[11px] text-[#1a1a1a]/60">Book a return meeting, carry over open items, notify attendees, add to their hubCMD calendars.</div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-[#1a1a1a]/40" /> : <ChevronRight className="w-4 h-4 text-[#1a1a1a]/40" />}
      </button>

      {open && (
        <div className="p-4 space-y-3 border-t border-[#1a1a1a]/06">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Meeting title *</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
                data-testid="followup-title"
                placeholder="e.g. Reconvene · Line 3 investigation follow-up"
                className="w-full bg-white border border-[#1a1a1a]/20 rounded-md px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Style</label>
              <select value={style} onChange={(e) => setStyle(e.target.value)}
                data-testid="followup-style"
                className="w-full bg-white border border-[#1a1a1a]/20 rounded-md px-3 py-1.5 text-sm">
                <option value="problem_solving">Problem Solving</option>
                <option value="management_review">Monthly Management Review</option>
                <option value="effectiveness_review">Effectiveness Review</option>
                <option value="safety">Safety Stand-Down</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">When *</label>
              <input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)}
                data-testid="followup-when"
                className="w-full bg-white border border-[#1a1a1a]/20 rounded-md px-3 py-1.5 text-sm" />
              <div className="text-[10px] font-mono text-[#1a1a1a]/45 mt-0.5">e.g. 3 days from now — enough time to investigate</div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Subject / discussion focus</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                data-testid="followup-subject"
                placeholder="What we need to check on / decide"
                className="w-full bg-white border border-[#1a1a1a]/20 rounded-md px-3 py-1.5 text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Attendees ({selectedAttendees.size} selected)</label>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-auto scrollbar-thin p-1.5 border border-[#1a1a1a]/10 rounded-md bg-white">
              {attendees.map((u) => {
                const sel = selectedAttendees.has(u.id);
                return (
                  <button key={u.id} type="button" onClick={() => toggleSet(setSelectedAttendees, selectedAttendees)(u.id)}
                    data-testid={`followup-attendee-${u.id}`}
                    className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${sel ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                    {u.name || u.email}
                  </button>
                );
              })}
              {attendees.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55 px-2">No prior attendees to carry over.</span>}
            </div>
          </div>

          {actions.length > 0 && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono font-bold">Actions to carry over ({carryOverIds.size} selected)</label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-auto scrollbar-thin p-1.5 border border-[#1a1a1a]/10 rounded-md bg-white">
                {actions.map((a) => {
                  const sel = carryOverIds.has(a.id);
                  return (
                    <button key={a.id} type="button" onClick={() => toggleSet(setCarryOverIds, carryOverIds)(a.id)}
                      data-testid={`followup-carryover-${a.id}`}
                      title={a.title}
                      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border ${sel ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#ff4500] border-[#ff4500]/30"}`}>
                      {a.display_id}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 items-center">
            <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)}
                data-testid="followup-notify-email"
                className="w-4 h-4 accent-[#ff4500]" />
              <span>Email attendees now</span>
            </label>
            <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={addCalendars} onChange={(e) => setAddCalendars(e.target.checked)}
                data-testid="followup-add-calendars"
                className="w-4 h-4 accent-[#ff4500]" />
              <span>Add to each attendee&apos;s hubCMD calendar</span>
            </label>
          </div>

          <div className="flex justify-end">
            <button onClick={schedule} disabled={busy}
              data-testid="followup-submit"
              className="btn-pill btn-pill-primary">
              <CalendarPlus className="w-3 h-3" /> {busy ? "Scheduling…" : "Schedule follow-up meeting"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared file / photo helpers — FIXED: use correct opscmd_token localStorage key
// ---------------------------------------------------------------------------
function _authedFileUrl(fid) {
  const token = localStorage.getItem("opscmd_token") || "";
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}?auth=${encodeURIComponent(token)}`;
}

function FileChip({ fid }) {
  return (
    <a href={_authedFileUrl(fid)} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-white border border-[#1a1a1a]/15 px-2 py-0.5 rounded-full hover:bg-[#fff1ea]">
      <FileText className="w-2.5 h-2.5" /> file
    </a>
  );
}

function FileChip2({ f }) {
  return (
    <a href={_authedFileUrl(f.id)} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-white border border-[#1a1a1a]/15 px-2 py-0.5 rounded-full hover:bg-[#fff1ea]">
      <FileText className="w-2.5 h-2.5" />
      <span className="truncate max-w-[140px]">{f.original_filename || f.id?.slice(0,8)}</span>
    </a>
  );
}

function PhotoThumb({ f }) {
  const isImage = (f.content_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|heic)$/i.test(f.original_filename || "");
  if (!isImage) return <FileChip2 f={f} />;
  return (
    <a href={_authedFileUrl(f.id)} target="_blank" rel="noreferrer" title={f.original_filename}>
      <img src={_authedFileUrl(f.id)} alt=""
        className="w-16 h-16 object-cover rounded-md border border-[#1a1a1a]/15 hover:border-[#ff4500]" />
    </a>
  );
}

function FileRow({ f }) {
  return (
    <li className="px-4 py-2">
      <a href={_authedFileUrl(f.id)} target="_blank" rel="noreferrer"
        className="text-[12px] text-[#1a1a1a] hover:text-[#ff4500] flex items-center gap-1.5 truncate">
        {(f.content_type || "").startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5 flex-shrink-0" /> : <FileText className="w-3.5 h-3.5 flex-shrink-0" />}
        <span className="truncate">{f.original_filename || f.id}</span>
      </a>
    </li>
  );
}


// Iter31 — Surface Verification & Validation items due within 30 days in
// Monthly Management Review meetings. Discussion here typically covers
// ISO/SQF program reviews, so this card auto-lists what's coming up.
function UpcomingVVPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/vv-items/upcoming", { params: { days: 30 } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="surface-card rounded-2xl overflow-hidden" data-testid="upcoming-vv-panel">
      <div className="bg-[#a13ad6] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-1">
        <ClipboardList className="w-3 h-3" /> V&amp;V due next 30 days ({items.length})
      </div>
      <ul className="divide-y divide-[#1a1a1a]/06 max-h-64 overflow-auto scrollbar-thin">
        {items.map((v) => (
          <li key={v.id} className="px-4 py-2" data-testid={`upcoming-vv-${v.id}`}>
            <Link to={`/app/vv/${v.id}`} target="_blank" rel="noreferrer"
              className="text-[12px] font-medium text-[#1a1a1a] hover:text-[#a13ad6] flex items-start gap-1.5">
              <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block truncate">{v.title}</span>
                <span className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                  {v.display_id} · due {formatDate(v.due_date)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}


// iter60 — Reschedule / edit a scheduled meeting. Fires updated ICS + save-the-date.
// iter62 — Also usable during LIVE (in_progress) meetings to edit title/agenda — no ICS resend.
function RescheduleMeetingDialog({ meeting, onClose, onSaved }) {
  const isLive = meeting.status === "in_progress";
  const [title, setTitle] = useState(meeting.title || "");
  const [scheduledFor, setScheduledFor] = useState((meeting.scheduled_for || "").slice(0, 16));
  const [agenda, setAgenda] = useState(meeting.agenda_items || []);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        agenda_items: agenda,
      };
      if (!isLive) payload.scheduled_for = scheduledFor ? new Date(scheduledFor).toISOString() : null;
      await api.patch(`/formal-meetings/${meeting.id}`, payload);
      toast.success(isLive ? "Meeting details updated" : "Rescheduled — updated .ics sent to attendees.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };
  const addAgenda = () => {
    if (!newTitle.trim()) return;
    setAgenda([...agenda, { title: newTitle.trim(), description: newDesc.trim() }]);
    setNewTitle(""); setNewDesc("");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="reschedule-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold">// {isLive ? "Edit live meeting" : "Reschedule meeting"}</div>
          <DialogTitle className="font-display font-black text-lg">Edit {meeting.display_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[12px] max-h-[60vh] overflow-auto pr-1">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="reschedule-title"
              className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
          </div>
          {!isLive && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">New scheduled date/time</label>
              <input type="datetime-local" value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                data-testid="reschedule-datetime"
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70 font-mono">Agenda items ({agenda.length})</label>
            <ul className="mt-1 space-y-1">
              {agenda.map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-[#faf8f4] border border-[#1a1a1a]/06 text-[12px]">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{a.title}</div>
                    {a.description && <div className="text-[10px] text-[#1a1a1a]/60">{a.description}</div>}
                  </div>
                  <button type="button" onClick={() => setAgenda(agenda.filter((_, ix) => ix !== i))}
                    className="p-1 text-red-500 hover:bg-red-50 rounded">
                    <X className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-1.5 space-y-1 p-2 rounded-lg border border-dashed border-[#1a1a1a]/20">
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAgenda())}
                placeholder="New agenda title (Enter to add)"
                className="w-full border border-[#1a1a1a]/10 rounded px-2 py-1.5 text-[11px]" />
              <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Optional description"
                className="w-full border border-[#1a1a1a]/10 rounded px-2 py-1.5 text-[11px]" />
              <button type="button" onClick={addAgenda} disabled={!newTitle.trim()}
                className="btn-pill btn-pill-secondary text-[10px] disabled:opacity-40">+ Add</button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-secondary text-[11px]">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="reschedule-save"
            className="btn-pill btn-pill-primary text-[11px]">
            {busy ? "Saving…" : isLive ? "Save changes" : "Save & send updated invite"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// iter58 — Additional agenda items (free-form title + description) added at creation time.
// iter60 — When the meeting is LIVE, each row expands into a discussion thread with notes/files
// scoped to that agenda_item_id.
function AgendaItemsPanel({ items, meetingId, isLive }) {
  const [expandedId, setExpandedId] = useState(null);
  const [threads, setThreads] = useState({});   // agenda_item_id → note[]
  const [drafts, setDrafts] = useState({});     // agenda_item_id → draft text
  const [draftFiles, setDraftFiles] = useState({});   // agenda_item_id → file id[]
  const [busy, setBusy] = useState(false);
  const sorted = [...(items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const loadThread = async (agendaId) => {
    try {
      const r = await api.get(`/formal-meetings/${meetingId}/notes`, { params: { agenda_item_id: agendaId } });
      setThreads((t) => ({ ...t, [agendaId]: r.data || [] }));
    } catch { /* silent */ }
  };
  const openRow = (a) => {
    const key = a.id;
    if (expandedId === key) { setExpandedId(null); return; }
    setExpandedId(key);
    if (!threads[key]) loadThread(key);
  };

  const postNote = async (a) => {
    const text = (drafts[a.id] || "").trim();
    const fids = (draftFiles[a.id] || []);
    if (!text && fids.length === 0) return toast.error("Type a note or attach a file first");
    setBusy(true);
    try {
      await api.post(`/formal-meetings/${meetingId}/notes`, {
        text: text || `${fids.length} file(s) attached`,
        agenda_item_id: a.id,
        file_ids: fids,
      });
      setDrafts((d) => ({ ...d, [a.id]: "" }));
      setDraftFiles((d) => ({ ...d, [a.id]: [] }));
      await loadThread(a.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-2xl overflow-hidden" data-testid="agenda-items-panel">
      <div className="bg-[#1a1a1a] text-white px-4 py-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#d6a900] font-bold">// Additional Agenda</div>
        <div className="font-display font-black text-sm">{sorted.length} topic{sorted.length === 1 ? "" : "s"}{isLive && " · live threads"}</div>
      </div>
      <ul className="divide-y divide-[#1a1a1a]/06">
        {sorted.map((a, i) => (
          <li key={a.id || i} data-testid={`agenda-item-${i}`}>
            <button
              onClick={() => openRow(a)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-[#faf8f4]"
              data-testid={`agenda-toggle-${a.id}`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="font-mono font-bold text-[10px] text-[#1a1a1a]/60">#{i + 1}</span>
                <span className="font-display font-bold text-[13px] text-[#1a1a1a] truncate">{a.title}</span>
                {isLive && (threads[a.id] || []).length > 0 && (
                  <span className="ml-1 inline-block bg-[#d6a900] text-white font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full">
                    {(threads[a.id] || []).length}
                  </span>
                )}
              </div>
              {expandedId === a.id
                ? <ChevronDown className="w-3 h-3 text-[#1a1a1a]/50 flex-shrink-0" />
                : <ChevronRight className="w-3 h-3 text-[#1a1a1a]/50 flex-shrink-0" />}
            </button>
            {expandedId === a.id && (
              <div className="px-4 pb-3 pt-1 space-y-2 bg-[#faf8f4]/40">
                {a.description && (
                  <div className="text-[12px] text-[#1a1a1a]/80 whitespace-pre-wrap italic border-l-2 border-[#d6a900] pl-2">
                    {a.description}
                  </div>
                )}
                {/* Thread */}
                {isLive && (
                  <>
                    <ul className="space-y-1.5">
                      {(threads[a.id] || []).map((n) => (
                        <li key={n.id} className="text-[12px] bg-white border border-[#1a1a1a]/08 rounded-lg p-2"
                          data-testid={`agenda-note-${n.id}`}>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                            {n.user_name} · {formatDate(n.created_at)}
                          </div>
                          <div className="text-[#1a1a1a] whitespace-pre-wrap mt-0.5">{n.text}</div>
                          {(n.file_ids || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {(n.file_ids || []).map((fid) => (
                                <a key={fid}
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}?auth=${encodeURIComponent(localStorage.getItem("opscmd_token") || "")}`}
                                  target="_blank" rel="noreferrer"
                                  data-testid={`agenda-note-file-${fid}`}
                                  className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#1a5fb4] hover:underline bg-[#1a5fb4]/10 rounded-full px-2 py-0.5">
                                  <Paperclip className="w-3 h-3" /> file
                                </a>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                      {(threads[a.id] || []).length === 0 && (
                        <li className="text-[10px] text-[#1a1a1a]/45 italic">No notes yet — start the discussion below.</li>
                      )}
                    </ul>
                    <div className="pt-1 space-y-1.5">
                      <textarea
                        value={drafts[a.id] || ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                        placeholder="Add a discussion note pinned to this agenda topic…"
                        rows={2}
                        data-testid={`agenda-note-input-${a.id}`}
                        className="w-full border border-[#1a1a1a]/10 rounded-lg px-2 py-1.5 text-[11px] resize-none"
                      />
                      <PhotoUploader
                        photoIds={draftFiles[a.id] || []}
                        onChange={(ids) => setDraftFiles((d) => ({ ...d, [a.id]: ids }))}
                        testIdPrefix={`agenda-note-file-${a.id}`}
                        label="Attach evidence"
                      />
                      <div className="flex justify-end">
                        <button onClick={() => postNote(a)} disabled={busy || (!(drafts[a.id] || "").trim() && !(draftFiles[a.id] || []).length)}
                          data-testid={`agenda-note-post-${a.id}`}
                          className="btn-pill btn-pill-primary text-[10px] disabled:opacity-40">
                          Post to thread
                        </button>
                      </div>
                    </div>
                  </>
                )}
                {!isLive && (
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/40">
                    Threads open once the meeting starts.
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}


// iter37 — Attached V&V items panel + picker on the Formal Meeting Room.
function AttachedVVPanel({ meetingId }) {
  const [attached, setAttached] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => {
    // Meeting doc has linked_vv_item_ids on it; fetch fresh via /formal-meetings
    api.get(`/formal-meetings/${meetingId}`)
      .then(async (r) => {
        const ids = r.data?.linked_vv_item_ids || [];
        if (!ids.length) { setAttached([]); return; }
        const results = await Promise.all(ids.map((vid) =>
          api.get(`/vv-items/${vid}`).catch(() => null)));
        setAttached(results.filter(Boolean).map((x) => x.data));
      })
      .catch(() => setAttached([]));
  };
  useEffect(() => { load(); }, [meetingId]);  // eslint-disable-line

  const unlink = async (vid) => {
    await api.delete(`/vv-items/${vid}/link-meeting/${meetingId}`);
    toast.success("Detached");
    load();
  };

  return (
    <div className="surface-card rounded-2xl overflow-hidden" data-testid="attached-vv-panel">
      <div className="bg-[#a13ad6] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center justify-between">
        <span className="flex items-center gap-1"><ClipboardList className="w-3 h-3" /> V&amp;V attached ({attached.length})</span>
        <button onClick={() => setPickerOpen(true)} data-testid="attach-vv-btn"
          className="text-[10px] uppercase tracking-wider font-bold underline">+ Attach</button>
      </div>
      {attached.length === 0 ? (
        <div className="px-4 py-3 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">None attached</div>
      ) : (
        <div className="p-3 space-y-3">
          {attached.map((v) => (
            <VVIndexCard key={v.id} vv={v} meetingId={meetingId} onUnlink={() => unlink(v.id)} onReload={load} />
          ))}
        </div>
      )}
      {pickerOpen && (
        <AttachVVDialog meetingId={meetingId} onClose={() => setPickerOpen(false)}
          onAttached={load} alreadyIds={new Set(attached.map((a) => a.id))} />
      )}
    </div>
  );
}

// iter53 — V&V rendered as its own index card inside a meeting.
// Shows description, expandable subtasks per section, and per-subtask comments/files.
// iter57 — Also renders open Occurrences with a one-click "Sign here" button that auto-links
// the sign-off to THIS meeting, closing the audit loop from inside the meeting room.
function VVIndexCard({ vv, meetingId, onUnlink, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [openSub, setOpenSub] = useState(null);  // subtask id whose comments are expanded
  const [commentDraft, setCommentDraft] = useState({});
  const [busyId, setBusyId] = useState(null);
  const { user } = useAuth();
  const subs = vv.sub_tasks || [];
  const bySection = {
    verification: subs.filter((s) => s.section === "verification"),
    validation:   subs.filter((s) => s.section === "validation"),
    tips:         subs.filter((s) => s.section === "tips"),
  };

  const openOccs = (vv.occurrences || []).filter((o) => o.status !== "completed")
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const signedOccs = (vv.occurrences || []).filter((o) => o.status === "completed");
  const signedHere = signedOccs.filter((o) => o.linked_meeting_id === meetingId);

  const signOccurrence = async (occ) => {
    const name = user?.name || window.prompt("Type your name to sign this occurrence:", "");
    if (!name || !name.trim()) return;
    setBusyId(occ.id);
    try {
      await api.post(`/vv-items/${vv.id}/occurrences/${occ.id}/complete`, {
        signature_name: name.trim(),
        meeting_id: meetingId,
      });
      toast.success(`Occurrence ${occ.due_date} signed & linked to this meeting`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally {
      setBusyId(null);
    }
  };

  const postComment = async (stid) => {
    const text = (commentDraft[stid] || "").trim();
    if (!text) return;
    setBusyId(stid);
    try {
      const st = subs.find((x) => x.id === stid);
      const nextNotes = ((st?.notes || "") + `\n[${new Date().toISOString().slice(0, 16).replace("T", " ")}] ${text}`).trim();
      await api.patch(`/vv-items/${vv.id}/sub-tasks/${stid}`, { notes: nextNotes });
      setCommentDraft({ ...commentDraft, [stid]: "" });
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to comment");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid={`vv-index-card-${vv.id}`} className="rounded-2xl border border-[#a13ad6]/25 bg-white overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 bg-[#a13ad6]/06 border-b border-[#a13ad6]/15 flex items-start gap-2">
        <button onClick={() => setExpanded(!expanded)} className="mt-1 shrink-0 text-[#a13ad6]"
          data-testid={`vv-expand-${vv.id}`}>
          {expanded ? "▾" : "▸"}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[9px] text-[#a13ad6] font-bold uppercase tracking-wider">{vv.display_id}</span>
            <Link to={`/app/vv/${vv.id}`} target="_blank" rel="noreferrer"
              className="font-display font-bold text-[13px] text-[#1a1a1a] hover:text-[#a13ad6] truncate">
              {vv.title}
            </Link>
            <span className="ml-auto text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">{vv.status}</span>
          </div>
          {vv.description && <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5">{vv.description}</div>}
          <div className="flex items-center gap-3 text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 mt-1">
            <span>V · {bySection.verification.length}</span>
            <span>Val · {bySection.validation.length}</span>
            <span>Tips · {bySection.tips.length}</span>
            {vv.due_date && <span>Due {vv.due_date.slice(0, 10)}</span>}
            {openOccs.length > 0 && <span className="text-[#ff4500] font-bold">{openOccs.length} open occ</span>}
            {signedHere.length > 0 && <span className="text-[#1a8754] font-bold">{signedHere.length} signed here</span>}
          </div>
        </div>
        <button onClick={onUnlink} className="p-1 text-red-500 hover:bg-red-50 rounded shrink-0"
          data-testid={`detach-vv-${vv.id}`} title="Detach from meeting">
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* iter57 — In-meeting occurrence sign panel — always visible when there are open occurrences */}
      {openOccs.length > 0 && (
        <div className="bg-[#fff1ea] border-b border-[#ff4500]/20 px-3 py-2" data-testid={`vv-inflow-sign-${vv.id}`}>
          <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#ff4500] font-bold mb-1.5 flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> Open occurrences · sign in-flow
          </div>
          <ul className="space-y-1.5">
            {openOccs.slice(0, 3).map((occ) => (
              <li key={occ.id} className="flex items-center justify-between gap-2 text-[11px]"
                data-testid={`vv-inflow-occ-${occ.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="font-display font-bold">{(occ.due_date || "").slice(0, 10)}</span>
                  <span className="text-[10px] text-[#1a1a1a]/55 ml-2">occurrence #{(vv.occurrences || []).indexOf(occ) + 1}</span>
                </div>
                <button
                  onClick={() => signOccurrence(occ)}
                  disabled={busyId === occ.id}
                  data-testid={`vv-inflow-sign-btn-${occ.id}`}
                  className="btn-pill btn-pill-primary text-[10px] disabled:opacity-50 shrink-0"
                >
                  <ShieldCheck className="w-3 h-3" /> {busyId === occ.id ? "Signing…" : "Sign here"}
                </button>
              </li>
            ))}
            {openOccs.length > 3 && (
              <li className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                +{openOccs.length - 3} more · <Link to={`/app/vv/${vv.id}`} target="_blank" rel="noreferrer" className="underline">open the V&amp;V card</Link>
              </li>
            )}
          </ul>
        </div>
      )}

      {expanded && (
        <div className="p-3 space-y-3">
          {[["verification", "Verification", "#1a5fb4"], ["validation", "Validation", "#a13ad6"], ["tips", "Tips", "#d6a900"]].map(([key, label, color]) => (
            <div key={key}>
              <div className="text-[10px] font-mono uppercase tracking-wider font-bold mb-1" style={{ color }}>
                {label} ({bySection[key].length})
              </div>
              {bySection[key].length === 0 ? (
                <div className="text-[11px] text-[#1a1a1a]/40 italic pl-2">No sub-tasks yet.</div>
              ) : (
                <ul className="space-y-1">
                  {bySection[key].map((s) => {
                    const isOpen = openSub === s.id;
                    return (
                      <li key={s.id} className="border border-[#1a1a1a]/08 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setOpenSub(isOpen ? null : s.id)}
                          data-testid={`vv-sub-toggle-${s.id}`}
                          className="w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-[#f7f5f2]"
                        >
                          <span className="text-[#1a1a1a]/40 text-xs">{isOpen ? "▾" : "▸"}</span>
                          <span className={`text-[12px] flex-1 ${s.status === "completed" ? "line-through text-[#1a1a1a]/50" : "text-[#1a1a1a]"}`}>
                            {s.title}
                          </span>
                          <span className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">{s.status}</span>
                        </button>
                        {isOpen && (
                          <div className="border-t border-[#1a1a1a]/06 bg-[#faf9f7] p-2 space-y-2">
                            {s.description && (
                              <div className="text-[11px] text-[#1a1a1a]/70">{s.description}</div>
                            )}
                            {s.notes && (
                              <div>
                                <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 mb-1">Comments log</div>
                                <pre className="text-[10px] whitespace-pre-wrap text-[#1a1a1a]/80 bg-white border border-[#1a1a1a]/08 rounded p-2 max-h-40 overflow-y-auto font-sans">
{s.notes}
                                </pre>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <input
                                data-testid={`vv-sub-comment-input-${s.id}`}
                                value={commentDraft[s.id] || ""}
                                onChange={(e) => setCommentDraft({ ...commentDraft, [s.id]: e.target.value })}
                                onKeyDown={(e) => e.key === "Enter" && postComment(s.id)}
                                placeholder="Add a comment (recorded with timestamp)…"
                                className="flex-1 text-[11px] border border-[#1a1a1a]/15 rounded px-2 py-1"
                              />
                              <button
                                data-testid={`vv-sub-comment-post-${s.id}`}
                                disabled={busyId === s.id}
                                onClick={() => postComment(s.id)}
                                className="text-[10px] font-bold uppercase tracking-wider bg-[#1a1a1a] text-white px-2 py-1 rounded disabled:opacity-40"
                              >Post</button>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function AttachVVDialog({ meetingId, onClose, onAttached, alreadyIds }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/vv-items").then((r) => setItems((r.data || []).filter((v) => !alreadyIds.has(v.id))));
  }, [alreadyIds]);

  const attach = async (vid) => {
    setBusy(true);
    try {
      await api.post(`/vv-items/${vid}/link-meeting`, { meeting_id: meetingId });
      toast.success("Attached");
      onAttached();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const shown = items.filter((v) => !q || (v.title || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 shadow-lift" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-display font-black text-lg">Attach V&amp;V item</div>
          <button onClick={onClose} className="p-1 text-[#1a1a1a]/50"><X className="w-4 h-4" /></button>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
          data-testid="attach-vv-search"
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm mb-2" />
        <div className="max-h-80 overflow-auto space-y-1">
          {shown.length === 0 && <div className="text-[11px] font-mono text-[#1a1a1a]/40 py-4 text-center">Nothing to attach</div>}
          {shown.map((v) => (
            <button key={v.id} disabled={busy} onClick={() => attach(v.id)}
              data-testid={`attach-vv-choose-${v.id}`}
              className="w-full text-left p-2.5 rounded-lg border border-[#1a1a1a]/10 hover:border-[#a13ad6] hover:bg-[#faf0ff] disabled:opacity-50">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] font-bold text-[#a13ad6]">{v.display_id}</span>
                <span className="text-[9px] uppercase text-[#1a1a1a]/50">{v.status}</span>
              </div>
              <div className="text-sm font-display font-bold">{v.title}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

